import { createContext, useContext, useState, useEffect, useCallback, ReactNode } from 'react';
import { useAuth } from './AuthContext';
import { chatService } from '../services/chatService';
import { profileResolver } from '../services/profileResolver';
import { notificationManager } from '../lib/notificationManager';
import { Conversation, Message } from '../types/chat';
import { useChatStore } from '../stores/chatStore';

// ===== TYPES =====

interface ChatContextValue {
    conversations: Conversation[];
    unreadCount: number;
    isConnected: boolean;
    sendMessage: (recipient: string, content: string) => Promise<void>;
    markAsRead: (pubkey: string) => void;
    fetchConversation: (pubkey: string) => Promise<Message[]>;
    refreshConversations: () => Promise<void>;
    hideConversation: (pubkey: string) => void;
    isSoundEnabled: boolean;
    toggleSound: () => void;
    lastReceivedMessage: Message | null;
    sendTyping: (recipient: string, isTyping: boolean) => Promise<void>;
}

const ChatContext = createContext<ChatContextValue | undefined>(undefined);

// ===== PROVIDER =====

export function ChatProvider({ children }: { children: ReactNode }) {
    const { pubkey, sk, loginMethod } = useAuth();

    const [conversations, setConversations] = useState<Conversation[]>([]);
    const [unreadCount, setUnreadCount] = useState(0);
    const [isConnected, setIsConnected] = useState(false);
    const [isSoundEnabled, setIsSoundEnabled] = useState(notificationManager.isSoundEnabled());
    const [lastReceivedMessage, setLastReceivedMessage] = useState<Message | null>(null);
    const [hiddenPubkeys, setHiddenPubkeys] = useState<Set<string>>(() => {
        const saved = localStorage.getItem('chat_hidden_pubkeys');
        return saved ? new Set(JSON.parse(saved)) : new Set();
    });

    // Presence heartbeat
    useEffect(() => {
        if (!pubkey || !loginMethod || conversations.length === 0) return;

        const broadcastPresence = () => {
            conversations.forEach(convo => {
                chatService.sendPresence(convo.pubkey, loginMethod, sk || undefined);
            });
        };

        broadcastPresence();
        const interval = setInterval(broadcastPresence, 60000); // Pulse every 60s
        return () => clearInterval(interval);
    }, [pubkey, loginMethod, sk, conversations.length]);

    // ===== CONVERSATION MANAGEMENT =====

    const loadConversations = useCallback(async () => {
        if (!pubkey) {
            setConversations([]);
            return;
        }

        try {
            console.log('🔄 [ChatContext] Refreshing conversation partners...');
            const partners = await chatService.getConversationPartners(pubkey);

            console.log(`👥 [ChatContext] Found ${partners.length} unique partners.`);

            if (partners.length === 0) {
                setConversations([]);
                return;
            }

            const profiles = await profileResolver.batchResolve(partners.map(p => p.pubkey));
            const convos: Conversation[] = [];

            for (const partner of partners) {
                if (hiddenPubkeys.has(partner.pubkey)) continue;

                const profile = profiles.get(partner.pubkey);
                if (!profile) continue;

                convos.push({
                    pubkey: partner.pubkey,
                    name: profile.name,
                    avatar: profile.avatar || null,
                    lastMessage: partner.lastMessage,
                    timestamp: partner.timestamp,
                    unread: 0,
                });
            }

            setConversations(convos);
        } catch (error) {
            console.error('❌ [ChatContext] loadConversations error:', error);
        }
    }, [pubkey]);

    const refreshConversations = useCallback(async () => {
        await loadConversations();
    }, [loadConversations]);

    // ===== MESSAGE HANDLING =====

    const handleNewMessage = useCallback((message: any) => {
        const sender = message.sender;
        const recipient = message.recipient;
        const msgContent = message.content;
        const timestamp = message.timestamp;

        console.log(`📨 [ChatContext] New message detected: ${sender.slice(0, 8)} -> ${recipient.slice(0, 8)}`);

        if (message.type === 'typing') {
            const store = useChatStore.getState();
            store.setTyping(message.pubkey, message.isTyping);
            store.setPresence(message.pubkey, timestamp);
            return;
        }

        if (message.type === 'presence') {
            useChatStore.getState().setPresence(message.pubkey, timestamp);
            return;
        }

        // Update presence for any incoming message
        useChatStore.getState().setPresence(sender, timestamp);

        const newMessage: Message = {
            id: message.id,
            sender: sender,
            recipient: recipient,
            content: msgContent,
            timestamp: timestamp,
            sent: sender === pubkey
        };

        setLastReceivedMessage(newMessage);

        const partnerPubkey = sender === pubkey ? recipient : sender;

        // Auto-unhide if new message arrives
        if (hiddenPubkeys.has(partnerPubkey)) {
            setHiddenPubkeys(prev => {
                const next = new Set(prev);
                next.delete(partnerPubkey);
                localStorage.setItem('chat_hidden_pubkeys', JSON.stringify(Array.from(next)));
                return next;
            });
        }

        setConversations(prev => {
            const existing = prev.find(c => c.pubkey === partnerPubkey);

            if (existing) {
                return prev.map(c =>
                    c.pubkey === partnerPubkey
                        ? {
                            ...c,
                            lastMessage: msgContent,
                            timestamp: timestamp * 1000,
                            unread: sender === pubkey ? c.unread : c.unread + 1,
                        }
                        : c
                ).sort((a, b) => b.timestamp - a.timestamp);
            } else {
                profileResolver.resolveProfile(partnerPubkey).then(profile => {
                    const newConvo: Conversation = {
                        pubkey: partnerPubkey,
                        name: profile.name,
                        avatar: profile.avatar || null,
                        lastMessage: msgContent,
                        timestamp: timestamp * 1000,
                        unread: sender === pubkey ? 0 : 1,
                    };
                    setConversations(prev => {
                        if (prev.find(c => c.pubkey === partnerPubkey)) return prev;
                        return [newConvo, ...prev];
                    });
                });
                return prev;
            }
        });

        if (sender !== pubkey) {
            // Unread count is now auto-calculated via useEffect from conversations list
            profileResolver.resolveProfile(sender).then(profile => {
                const currentUnreadForConvo = conversations.find(c => c.pubkey === sender)?.unread || 0;
                notificationManager.notifyNewMessage(profile.name, msgContent.slice(0, 50), currentUnreadForConvo + 1);
            });
        }
    }, [pubkey]);

    const markAsRead = useCallback((targetPubkey: string) => {
        setConversations(prev => {
            const updated = prev.map(c =>
                c.pubkey === targetPubkey ? { ...c, unread: 0 } : c
            );
            return updated;
        });
    }, []);

    const sendMessage = useCallback(async (recipient: string, content: string) => {
        if (!pubkey || !loginMethod) throw new Error('Not logged in');

        // Stop typing immediately when sending
        chatService.sendTyping(recipient, false, loginMethod, sk || undefined);

        // Relay result is now awaited in chatService
        await chatService.sendMessage(recipient, content, loginMethod, sk || undefined, pubkey);

        // Play sent sound
        notificationManager.playSentSound();

        // Optimistic update for the conversation list
        setConversations(prev => {
            const existing = prev.find(c => c.pubkey === recipient);
            if (existing) {
                return prev.map(c =>
                    c.pubkey === recipient
                        ? { ...c, lastMessage: content, timestamp: Date.now() }
                        : c
                ).sort((a, b) => b.timestamp - a.timestamp);
            }
            return prev;
        });
    }, [pubkey, sk, loginMethod]);

    const sendTyping = useCallback(async (recipient: string, isTyping: boolean) => {
        if (!pubkey || !loginMethod) return;
        await chatService.sendTyping(recipient, isTyping, loginMethod, sk || undefined);
    }, [pubkey, sk, loginMethod]);

    const fetchConversation = useCallback(async (otherPubkey: string): Promise<Message[]> => {
        if (!pubkey || !loginMethod) return [];
        const messages = await chatService.fetchConversation(otherPubkey, pubkey, loginMethod, sk || undefined);

        // Update list with the last decrypted message
        if (messages.length > 0) {
            const lastMsg = messages[messages.length - 1];
            setConversations(prev => prev.map(c =>
                c.pubkey === otherPubkey
                    ? { ...c, lastMessage: lastMsg.content, timestamp: lastMsg.timestamp * 1000 }
                    : c
            ));
        }

        return messages;
    }, [pubkey, sk, loginMethod]);

    const hideConversation = useCallback((targetPubkey: string) => {
        setHiddenPubkeys(prev => {
            const next = new Set(prev);
            next.add(targetPubkey);
            localStorage.setItem('chat_hidden_pubkeys', JSON.stringify(Array.from(next)));
            return next;
        });
        setConversations(prev => prev.filter(c => c.pubkey !== targetPubkey));
    }, []);

    const toggleSound = useCallback(() => {
        setIsSoundEnabled(notificationManager.toggleSound());
    }, []);

    // ===== SUBSCRIPTION =====

    useEffect(() => {
        loadConversations();
    }, [loadConversations]);

    // Derived unread count
    useEffect(() => {
        const total = conversations.reduce((sum, c) => sum + c.unread, 0);
        setUnreadCount(total);
    }, [conversations]);

    useEffect(() => {
        if (!pubkey || !loginMethod) {
            setIsConnected(false);
            return;
        }

        console.log('🔌 [ChatContext] Subscribing globally...');
        setIsConnected(true);

        const unsubscribe = chatService.subscribeToMessages(
            pubkey,
            loginMethod,
            sk,
            handleNewMessage
        );

        return () => {
            unsubscribe();
            setIsConnected(false);
        };
    }, [pubkey, sk, loginMethod, handleNewMessage]);

    return (
        <ChatContext.Provider value={{
            conversations,
            unreadCount,
            isConnected,
            sendMessage,
            markAsRead,
            fetchConversation,
            refreshConversations,
            hideConversation,
            isSoundEnabled,
            toggleSound,
            lastReceivedMessage,
            sendTyping,
        }}>
            {children}
        </ChatContext.Provider>
    );
}

export function useChatContext() {
    const context = useContext(ChatContext);
    if (!context) throw new Error('useChatContext must be used within ChatProvider');
    return context;
}
